import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { getLocalTranslate } from 'src/app/helpers/global-functions';
import { AuthService } from 'src/app/services/auth.service';
import { Title } from '@angular/platform-browser';

function forgotPasswordMatchValidator(control: AbstractControl) {
  const formGroup = control as FormGroup;
  const password = formGroup.get('password');
  const confirmPassword = formGroup.get('confirmPassword');

  if (!password || !confirmPassword) {
    return null;
  }

  if (confirmPassword.errors && !confirmPassword.errors.mustMatch) {
    return null;
  }

  if (password.value !== confirmPassword.value) {
    confirmPassword.setErrors({ mustMatch: true });
  } else {
    confirmPassword.setErrors(null);
  }

  return null;
}

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css']
})
export class ForgotPasswordComponent implements OnInit {

  public showSpinner = false;

  forgotPasswordForm!: FormGroup;
  resetPasswordForm!: FormGroup;

  showOtpDialog = false;
  isSendingOtp = false;
  isVerifyingOtp = false;
  isUpdatingPassword = false;
  otpSent = false;
  otpVerified = false;
  otpCode = '';
  resetEmail = '';

  emailText:string = 'Email';
  forgotPassword:string = 'Forgot Password';
  forgotPasswordText:string = 'Forgot Password Text';
  submit:string = 'Submit';

  constructor(
    private authService: AuthService,
    private toastr: ToastrService,
    private router: Router,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    this.needTranslate();
    this.forgotPasswordForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.email
      ])
    });

    this.resetPasswordForm = new FormGroup({
      password: new FormControl('', [Validators.required, Validators.minLength(6)]),
      confirmPassword: new FormControl('', [Validators.required, Validators.minLength(6)])
    }, {
      validators: forgotPasswordMatchValidator
    });
  }

  needTranslate(){
    this.emailText = getLocalTranslate('email', this.emailText);
    this.forgotPassword = getLocalTranslate('forgot-password', this.forgotPassword);
    this.forgotPasswordText = getLocalTranslate('forgot-password-text', this.forgotPasswordText);
    this.submit = getLocalTranslate('submit', this.submit);
    this.titleService.setTitle(`${getLocalTranslate('forgot-password', this.forgotPassword)} | CamEdu`);
  }

  get email() { return this.forgotPasswordForm.get('email'); }
  get password() { return this.resetPasswordForm.get('password'); }
  get confirmPassword() { return this.resetPasswordForm.get('confirmPassword'); }

  resetOtpFlow(): void {
    this.showOtpDialog = false;
    this.otpSent = false;
    this.otpVerified = false;
    this.otpCode = '';
    this.resetEmail = '';
    this.isSendingOtp = false;
    this.isVerifyingOtp = false;
    this.isUpdatingPassword = false;
    this.resetPasswordForm.reset();
  }

  async sendOtpToEmail(): Promise<void> {
    const email = this.email?.value?.trim();

    if (!this.email || !this.email.valid || !email) {
      this.toastr.error('Please enter a valid email address.');
      return;
    }

    this.isSendingOtp = true;
    this.resetEmail = email;

    try {
      await this.authService.sendPasswordResetOtp(this.resetEmail);
      this.otpSent = true;
      this.showOtpDialog = true;
      this.isSendingOtp = false;
      this.toastr.success('OTP code has been sent to your email.');
    } catch (error: any) {
      this.isSendingOtp = false;
      this.toastr.error(error?.message || 'Failed to send OTP.');
    }
  }

  async verifyOtpCode(): Promise<void> {
    if (!this.otpCode || this.otpCode.length !== 4) {
      this.toastr.error('Please enter the 4-digit code.');
      return;
    }

    this.isVerifyingOtp = true;

    try {
      await this.authService.verifyPasswordResetOtp(this.resetEmail, this.otpCode);
      this.otpVerified = true;
      this.isVerifyingOtp = false;
      this.toastr.success('OTP verified successfully.');
    } catch (error: any) {
      this.isVerifyingOtp = false;
      this.toastr.error(error?.message || 'Invalid or expired OTP.');
    }
  }

  async updatePassword(): Promise<void> {
    if (this.resetPasswordForm.invalid) {
      this.toastr.error('Please enter a valid new password and confirm it.');
      return;
    }

    if (!this.otpVerified) {
      this.toastr.error('Please verify the OTP first.');
      return;
    }

    const newPassword = this.password?.value ?? '';
    this.isUpdatingPassword = true;

    try {
      await this.authService.resetPasswordWithOtp(this.resetEmail, this.otpCode, newPassword);
      this.isUpdatingPassword = false;
      this.toastr.success('Password updated successfully.');
      this.forgotPasswordForm.reset();
      this.resetOtpFlow();
      this.router.navigate(['login']);
    } catch (error: any) {
      this.isUpdatingPassword = false;
      this.toastr.error(error?.message || 'Failed to update password.');
    }
  }

  async onSubmit() {
    if (!this.forgotPasswordForm.valid) {
      return;
    }

    const email = this.email?.value?.trim();
    if (!email) {
      this.toastr.error('Please enter your email!');
      return;
    }

    if (!this.email || !this.email.valid) {
      this.toastr.error('Please enter correct email!');
      return;
    }

    this.showSpinner = true;
    this.sendOtpToEmail();
    this.showSpinner = false;
  }
}
